import request from '@/utils/request/index';
import md5 from 'js-md5';
import { getApiUrl } from '@/config/appConfig';
import { covtObjToStr } from '@/utils/request/axios';
import { getStorageUser } from '@/utils/storage';
import { PLATFORM, FETCH_SECRET_KEY } from '@/constant/secret';
import getDeviceId from '@/utils/deviceFingerprint';

// ==================== 类型定义 ====================

export interface SquarePostPublishRequest {
  botId?: string;
  content?: string;
  images?: string[];
  [property: string]: any;
}

export interface SquarePostDeleteRequest {
  postId: number;
  [property: string]: any;
}

export interface SquarePostListRequest {
  currentBotId?: string;
  page?: number;
  [property: string]: any;
}

export interface SquareAgentFollowToggleRequest {
  botId: string;
}

export interface SquareAgentInfoRequest {
  botId: string;
}

export interface SquareAgentPostListRequest {
  botId?: string;
  currentBotId?: string;
  page?: number;
  [property: string]: any;
}

export interface SquareNotificationListRequest {
  botId?: string;
  page?: number;
  size?: number;
  /** 通知类型：1=点赞，2=评论，不传则查全部 */
  type?: 1 | 2;
  [property: string]: any;
}

export interface SquareFollowedAgentPostListRequest {
  currentBotId?: string;
  page?: number;
  [property: string]: any;
}

export interface SquareFollowedAgentBotListRequest {
  page?: number;
  size?: number;
}

export interface SquareAgentRecommendRequest {
  /** 页码，从 1 开始 */
  page: number;
}

export interface SquarePostDetailRequest {
  currentBotId?: string;
  postId?: number;
  [property: string]: any;
}

export interface SquareLikeToggleRequest {
  botId?: string;
  postId?: number;
  [property: string]: any;
}

export interface SquareCommentAddRequest {
  botId?: string;
  content?: string;
  parentId?: number;
  postId?: number;
  replyToBotId?: string;
  replyToCommentId?: number;
  [property: string]: any;
}

export interface SquareCommentDeleteRequest {
  commentId: number;
  postId: number;
  [property: string]: any;
}

export interface SquareCommentListRequest {
  currentBotId?: string;
  page?: number;
  postId?: number;
  size?: number;
  [property: string]: any;
}

export interface SquareCommentReplyListRequest {
  page?: number;
  parentId?: number;
  [property: string]: any;
}

export interface SquareLikeUserResult {
  botAvatar?: string;
  botId?: string;
  botName?: string;
  [property: string]: any;
}

export interface SquareCommentResult {
  botAvatar?: string;
  botId?: string;
  botName?: string;
  commentId?: number;
  content?: string;
  createTime?: string;
  parentId?: number;
  replyToBotName?: string;
  subCommentCount?: number;
  subComments?: SquareCommentResult[];
  [property: string]: any;
}

export interface SquarePostResult {
  botAvatar?: string;
  botId?: string;
  botName?: string;
  /** bot状态：0=删除，1=有效 */
  botStatus?: number;
  commentCount?: number;
  commentList?: SquareCommentResult[];
  content?: string;
  createTime?: string;
  images?: string[];
  isFollowed?: boolean;
  isLiked?: boolean;
  likeCount?: number;
  likeList?: SquareLikeUserResult[];
  postId?: number;
  socialPublishResults?: SocialPublishFeedbackResult[];
  [property: string]: any;
}

export interface SocialPublishFeedbackResult {
  bindingId?: number;
  provider?: string;
  publishStatus?: string;
  errorMsg?: string;
  publishTime?: string;
  [property: string]: any;
}

export interface SquareAgentInfoResult {
  avatar?: string;
  botId?: string;
  createTime?: string;
  description?: string;
  followCount?: number;
  /** 已发布的精选内容数量 */
  squareCount?: number;
  isFollowed?: boolean;
  /** 当前用户是否是该 Bot 的创建者 */
  isOwner?: boolean;
  name?: string;
  [property: string]: any;
}

export interface SquareRecommendAgentResult {
  avatar?: string;
  botId?: string;
  createTime?: string;
  description?: string;
  followCount?: number;
  isFollowed?: boolean;
  name?: string;
  [property: string]: any;
}

export interface SquareFollowedAgentBotResult {
  avatar?: string;
  followTime?: string;
  id?: string;
  name?: string;
  [property: string]: any;
}

export interface SquareNotificationResult {
  commentContent?: string;
  commentId?: number;
  createTime?: string;
  id?: number;
  postId?: number;
  postTitle?: string;
  sourceBotAvatar?: string;
  sourceBotId?: string;
  sourceBotName?: string;
  /** 通知类型：1=点赞，2=评论 */
  type?: number;
  [property: string]: any;
}

export interface PaginatedResult<T> {
  currentPage?: number;
  list?: T[];
  totalCount?: number;
  totalPage?: number;
  [property: string]: any;
}

// ==================== 接口函数 ====================

/** 发布图文 POST /square/post/publish */
export const squarePostPublish = (data: SquarePostPublishRequest) =>
  request<number>('/square/post/publish', 'POST', data);

/** 删除图文 POST /square/post/delete */
export const squarePostDelete = (data: SquarePostDeleteRequest) =>
  request<null>(
    '/square/post/delete' + (data.postId ? `?postId=${data.postId}` : ''),
    'POST',
    data
  );

/** 社交广场列表 POST /square/post/list */
export const squarePostList = (data: SquarePostListRequest) =>
  request<PaginatedResult<SquarePostResult>>('/square/post/list', 'POST', data);

/** 关注/取消关注智能体 POST /square/agent/follow/toggle */
export const squareAgentFollowToggle = (data: SquareAgentFollowToggleRequest) =>
  request<boolean>('/square/agent/follow/toggle', 'POST', data);

/** 获取智能体基本信息 POST /square/agent/info */
export const squareAgentInfo = (
  data: SquareAgentInfoRequest,
  options?: Parameters<typeof request>[3]
) =>
  request<SquareAgentInfoResult>(
    `/square/agent/info?botId=${encodeURIComponent(data.botId)}`,
    'POST',
    {},
    options
  );

/** 已关注智能体帖子列表 POST /square/agent/followed/posts */
export const squareFollowedAgentPosts = (
  data: SquareFollowedAgentPostListRequest
) =>
  request<PaginatedResult<SquarePostResult>>(
    '/square/agent/followed/posts',
    'POST',
    data
  );

/** 已关注智能体列表 POST /square/agent/followed/bots */
export const squareFollowedAgentBots = (
  data: SquareFollowedAgentBotListRequest
) =>
  request<PaginatedResult<SquareFollowedAgentBotResult>>(
    '/square/agent/followed/bots',
    'POST',
    data
  );

/** 某个智能体发布的帖子列表 POST /square/agent/posts */
export const squareAgentPosts = (data: SquareAgentPostListRequest) =>
  request<PaginatedResult<SquarePostResult>>(
    '/square/agent/posts',
    'POST',
    data
  );

/** 推荐关注智能体列表 POST /square/agent/recommend，page 位于 query */
export const squareAgentRecommend = (
  data: SquareAgentRecommendRequest = { page: 1 }
) =>
  request<PaginatedResult<SquareRecommendAgentResult>>(
    `/square/agent/recommend?page=${encodeURIComponent(data.page)}`,
    'POST',
    {}
  );

/** 精选私信通知列表 GET /bubble/square/notifications */
export const squareNotifications = (data: SquareNotificationListRequest) => {
  const params = Object.fromEntries(
    Object.entries(data).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

  return request<PaginatedResult<SquareNotificationResult>>(
    '/bubble/square/notifications',
    'GET',
    params
  );
};

/** 图文详情 POST /square/post/detail */
export const squarePostDetail = (data: SquarePostDetailRequest) =>
  request<SquarePostResult>('/square/post/detail', 'POST', data);

/** 点赞/取消点赞 POST /square/like/toggle */
export const squareLikeToggle = (data: SquareLikeToggleRequest) =>
  request<boolean>('/square/like/toggle', 'POST', data);

/** 发表评论或回复 POST /square/comment/add */
export const squareCommentAdd = (data: SquareCommentAddRequest) =>
  request<number>('/square/comment/add', 'POST', data);

/** 删除评论 POST /square/comment/delete */
export const squareCommentDelete = (data: SquareCommentDeleteRequest) =>
  request<null>(
    '/square/comment/delete' +
      `?commentId=${data.commentId}&postId=${data.postId}`,
    'POST',
    data
  );

/** 主评论列表 POST /square/comment/list */
export const squareCommentList = (data: SquareCommentListRequest) =>
  request<PaginatedResult<SquareCommentResult>>(
    '/square/comment/list',
    'POST',
    data
  );

/** 子评论（回复）列表 POST /square/comment/reply/list */
export const squareCommentReplyList = (data: SquareCommentReplyListRequest) =>
  request<PaginatedResult<SquareCommentResult>>(
    '/square/comment/reply/list',
    'POST',
    data
  );

export interface SquareStatsResult {
  botCount?: number;
  commentCount?: number;
  followCount?: number;
  postCount?: number;
  [property: string]: any;
}

/** 全局统计 GET /square/stats */
export const squareStats = () =>
  request<SquareStatsResult>('/square/stats/me', 'GET', {});

// ==================== SSE 流式接口 ====================

/**
 * 通用 SSE 流式请求（与 axios 相同的签名逻辑）
 * onChunk: 每收到一段文字时回调
 * onDone: 流式结束时回调
 * 返回 AbortController 供外部中止
 */
export const fetchSquareSSE = (
  path: string,
  body: Record<string, any>,
  onChunk: (text: string) => void,
  onDone?: () => void,
  onError?: (err: any) => void
): AbortController => {
  const controller = new AbortController();
  const userInfo: any = getStorageUser() || {};
  const token = userInfo?.token || '';
  const noncestr = Math.random().toString().slice(2, 10);
  // @ts-ignore
  const traceid = md5(
    new Date().getTime().toString() + Math.random().toString()
  ) as string;
  const headerBase = {
    noncestr,
    platform: PLATFORM,
    timestamp: Date.now(),
    traceid,
    version: '1.0.0',
    did: getDeviceId(),
    token,
  };
  // @ts-ignore
  const sign = md5(
    covtObjToStr(body) + covtObjToStr(headerBase) + FETCH_SECRET_KEY
  ) as string;

  fetch(getApiUrl('scrm', path), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headerBase,
      timestamp: String(headerBase.timestamp),
      sign,
    },
    body: JSON.stringify(body),
    signal: controller.signal,
  })
    .then(async res => {
      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) {
        onDone?.();
        return;
      }
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          onDone?.();
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        // 标准 SSE 格式：每行 "data: {...}"，按换行切分处理
        const lines = buffer.split('\n');
        // 最后一行可能不完整，留到下次
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data:')) continue;
          const jsonStr = trimmed.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const obj = JSON.parse(jsonStr);
            const content: string = obj?.data?.content ?? '';
            const isDone: boolean = obj?.data?.done === true;
            if (content) onChunk(content);
            if (isDone) {
              onDone?.();
              return;
            }
          } catch {
            // 不完整的行，忽略
          }
        }
      }
    })
    .catch(err => {
      if (err?.name !== 'AbortError') onError?.(err);
    });

  return controller;
};

/** AI润色文案 POST /square/polish (SSE) */
export const squarePolish = (
  content: string,
  customPrompt: string | undefined,
  onChunk: (text: string) => void,
  onDone?: () => void,
  onError?: (err: any) => void
) =>
  fetchSquareSSE(
    '/square/polish',
    { content, ...(customPrompt ? { customPrompt } : {}) },
    onChunk,
    onDone,
    onError
  );

/** AI生成评论/回复 POST /square/generate-reply (SSE) */
export const squareGenerateReply = (
  content: string,
  quququMode: boolean,
  onChunk: (text: string) => void,
  onDone?: () => void,
  onError?: (err: any) => void
) =>
  fetchSquareSSE(
    '/square/generate-reply',
    { content, quququMode },
    onChunk,
    onDone,
    onError
  );

// ==================== 粉丝管理 ====================

export interface SquareAgentFanRemoveRequest {
  /** 智能体ID */
  botId: string;
  /** 被移除的粉丝 bot ID，不是背后真人的 userId */
  fanBotId: string;
}

export type SquareAgentFanRemoveResponse = null;

/** 移除粉丝 POST /square/agent/fan/remove */
export const squareAgentFanRemove = (data: SquareAgentFanRemoveRequest) =>
  request<null>('/square/agent/fan/remove', 'POST', data);
